import moment from 'moment'
import i18n from '@/plugins/i18n'
import {
    COHORT_MONTHS, average, buildCohorts, clientLifespan, computeCac, computeRate, computeShiftsStats,
    currentCurrency, getAdsInvestment, isActiveAt, leftMonth, memberCount, monthOf, periodReference, shiftMembers,
    sumArea, sumExpenses, sumIncomes, topCounts,
} from '@/utils/Business'

const t = (key, params) => i18n.t(`business.${key}`, params)

const money = (key) => `${t(key)} (${currentCurrency()})`

const titleWithCurrency = (title) => `${title} · ${currentCurrency()}`

const common = (key) => i18n.t(`common.${key}`)

const monthLabel = (period) => moment(period, 'YYYY-MM').format('MMMM YYYY')

const dayName = (day) => moment().isoWeekday(Number(day)).format('dddd')

const yesNo = (value) => common(value ? 'yes' : 'no')

const blank = () => []

const section = (title) => [title]

export const buildClientsSheets = ({ roster = [], planTypes = [], payments = [], period = '' }) => {
    const planName = (id) => planTypes.find(plan => plan.id === id)?.name || t('no_plan')
    const reference = periodReference(period)
    const byId = {}
    roster.forEach(client => {
        if (client.id) byId[client.id] = client
    })

    const clients = [
        [
            t('client'), common('email'), t('source'), t('plan_type'), money('monthly_amount'), t('method'),
            t('joined_at'), t('next_payment'), t('seniority'), common('active'), t('sport'), t('area'),
            t('age'), t('gender'), t('job'), t('origin'),
        ],
        ...roster.map(client => [
            client.name,
            client.email,
            t(client.fromApp ? 'source_app' : 'source_manual'),
            client.planId ? planName(client.planId) : '',
            client.planAmount || '',
            client.method ? t(`method_${client.method}`) : '',
            client.joinedAt,
            client.nextPayment,
            clientLifespan(client, reference) ?? '',
            yesNo(client.active),
            client.sport,
            client.area,
            client.age || '',
            client.gender ? t(`gender_${{ M: 'male', F: 'female' }[client.gender] || 'other'}`) : '',
            client.job,
            client.origin ? t(`origin_${client.origin}`) : '',
        ]),
    ]

    const paid = [
        [t('client'), t('period'), money('amount'), t('method'), t('payment_date')],
        ...[...payments]
            .sort((a, b) => a.period.localeCompare(b.period))
            .map(payment => [
                byId[payment.clientId]?.name || payment.clientId,
                payment.period,
                payment.amount,
                payment.method ? t(`method_${payment.method}`) : '',
                payment.date,
            ]),
    ]

    return [
        { name: t('clients'), aoa: clients },
        { name: t('payments'), aoa: paid },
    ]
}

export const buildCashSheet = ({ cash, clientsIncome = 0, period = '' }) => {
    const rows = [
        section(titleWithCurrency(`${t('cash')} · ${monthLabel(period)}`)),
        blank(),
        [t('incomes'), '', ''],
        [t('clients_income'), '', clientsIncome],
        ...(cash?.incomes || []).map(item => [item.name, '', Number(item.amount) || 0]),
        [t('total'), '', sumIncomes(cash, clientsIncome)],
        blank(),
        [t('expenses'), '', ''],
    ]

    ;(cash?.expenses || []).forEach(area => {
        (area.items || []).forEach(item => rows.push([area.name, item.name, Number(item.amount) || 0]))
        rows.push([area.name, t('total'), sumArea(area)])
    })

    const incomes = sumIncomes(cash, clientsIncome)
    const expenses = sumExpenses(cash)
    rows.push([t('total'), '', expenses])
    rows.push(blank())
    rows.push([t('result'), '', incomes - expenses])
    rows.push([t('margin'), '', incomes ? `${Math.round((incomes - expenses) / incomes * 100)}%` : ''])

    return [{ name: t('cash'), aoa: rows }]
}

export const buildShiftsSheet = ({ shifts = [], activities = [], bookings = null, weekStart = '' }) => {
    const activityName = (id) => activities.find(item => item.id === id)?.name || t('no_activity')
    const stats = computeShiftsStats(shifts, bookings)
    const names = (shift, fixed) => shiftMembers(shift, bookings)
        .filter(member => member.fixed === fixed)
        .map(member => member.name)
        .join(', ')

    const rows = [
        section(t('schedule')),
        weekStart ? [t('week_of'), moment(weekStart, 'YYYY-MM-DD').format('DD/MM/YYYY')] : blank(),
        blank(),
        [
            t('activity'), t('day'), t('start_time'), t('end_time'), t('capacity'), t('members_column'),
            t('booking_fixed_short'), t('booking_once_short'),
        ],
        ...[...shifts]
            .sort((a, b) => a.day - b.day || a.startTime.localeCompare(b.startTime))
            .map(shift => [
                activityName(shift.activityId),
                dayName(shift.day),
                shift.startTime,
                shift.endTime,
                shift.capacity,
                memberCount(shift, bookings),
                names(shift, true),
                names(shift, false),
            ]),
        blank(),
        [t('taken_seats'), stats.taken],
        [t('occupancy'), `${stats.occupancy}%`],
        [t('full_shifts'), stats.full],
    ]

    return [{ name: t('schedule'), aoa: rows }]
}

const monthsOfYear = (year) => Array.from({ length: 12 }, (item, i) => `${year}-${String(i + 1).padStart(2, '0')}`)

const monthAmounts = (period, key, cash, months, clientsIncomeByPeriod) => {
    const monthCash = key === period ? cash : months[key]
    const autoIncome = clientsIncomeByPeriod[key] || 0
    const incomes = monthCash || autoIncome > 0 ? sumIncomes(monthCash, autoIncome) : null
    const expenses = monthCash ? sumExpenses(monthCash) : null
    return { incomes, expenses }
}

const buildBasicSheet = (data) => {
    const { roster, planTypes, payments, period, cash, months, clientsIncomeByPeriod } = data
    const reference = periodReference(period)
    const year = String(period).slice(0, 4)
    const current = moment().format('YYYY-MM')
    const periodPayments = payments.filter(payment => payment.period === period)
    const planName = (id) => planTypes.find(plan => plan.id === id)?.name || t('no_plan')

    const rows = [
        section(titleWithCurrency(`${t('stats_basic')} · ${monthLabel(period)}`)),
        blank(),
        [t('active_clients'), roster.filter(client => isActiveAt(client, reference)).length],
        [t('new_clients'), roster.filter(client => monthOf(client.joinedAt) === period).length],
        [t('lost_clients'), roster.filter(client => leftMonth(client) === period).length],
        [t('avg_ticket'), average(periodPayments.map(payment => Number(payment.amount) || 0)) ?? ''],
        blank(),
        section(t('evolution')),
        [
            t('period'), t('metric_active'), t('metric_joined'), t('metric_left'), t('metric_ticket'),
            t('metric_incomes'), t('metric_expenses'), t('metric_result'),
        ],
    ]

    monthsOfYear(year).forEach(key => {
        if (key > current) return
        const monthReference = periodReference(key)
        const { incomes, expenses } = monthAmounts(period, key, cash, months, clientsIncomeByPeriod)
        rows.push([
            key,
            roster.filter(client => isActiveAt(client, monthReference)).length,
            roster.filter(client => monthOf(client.joinedAt) === key).length,
            roster.filter(client => leftMonth(client) === key).length,
            average(payments.filter(payment => payment.period === key).map(p => Number(p.amount) || 0)) ?? '',
            incomes ?? '',
            expenses ?? '',
            incomes === null && expenses === null ? '' : (incomes || 0) - (expenses || 0),
        ])
    })

    rows.push(blank())
    rows.push(section(t('plans')))
    rows.push([t('plan_type'), t('by_clients'), t('by_billing')])

    const groups = [...planTypes.map(plan => ({ id: plan.id, name: plan.name })), { id: null, name: t('no_plan') }]
    groups.forEach(group => {
        const count = roster
            .filter(client => isActiveAt(client, reference) && (client.planId || null) === group.id).length
        const billed = periodPayments
            .filter(payment => (payment.planId || null) === group.id)
            .reduce((total, payment) => total + (Number(payment.amount) || 0), 0)
        if (count === 0 && billed === 0) return
        rows.push([group.name || planName(group.id), count, billed])
    })

    return { name: t('stats_basic'), aoa: rows }
}

const buildFinancialSheet = (data) => {
    const { period, cash, months, clientsIncomeByPeriod } = data
    const year = String(period).slice(0, 4)
    const incomes = sumIncomes(cash, clientsIncomeByPeriod[period] || 0)
    const expenses = sumExpenses(cash)

    const rows = [
        section(titleWithCurrency(`${t('stats_financial')} · ${monthLabel(period)}`)),
        blank(),
        [t('incomes'), incomes],
        [t('expenses'), expenses],
        [t('result'), incomes - expenses],
        [t('margin'), incomes ? `${Math.round((incomes - expenses) / incomes * 100)}%` : ''],
        blank(),
        section(t('yearly_evolution')),
        [t('period'), t('incomes'), t('expenses'), t('result')],
    ]

    monthsOfYear(year).forEach(key => {
        const month = monthAmounts(period, key, cash, months, clientsIncomeByPeriod)
        if (month.incomes === null && month.expenses === null) return
        rows.push([
            key,
            month.incomes ?? '',
            month.expenses ?? '',
            (month.incomes || 0) - (month.expenses || 0),
        ])
    })

    return { name: t('stats_financial'), aoa: rows }
}

const ageBands = (roster = []) => {
    const ages = roster.map(client => Number(client.age)).filter(age => age >= 5)
    const bands = []
    for (let from = 5; from <= 60; from += 5) bands.push({ from, to: from + 4 })
    bands.push({ from: 65, to: null })
    return bands
        .map(band => ({
            label: band.to ? `${band.from}-${band.to}` : `${band.from}+`,
            value: ages.filter(age => age >= band.from && (!band.to || age <= band.to)).length,
        }))
        .filter(item => item.value > 0)
}

const buildAudienceSheet = (data) => {
    const { roster, period, sportLabel } = data
    const reference = periodReference(period)
    const active = roster.filter(client => isActiveAt(client, reference))
    const withPlan = active.filter(client => (Number(client.planAmount) || 0) > 0)
    const lifespans = active.map(client => clientLifespan(client, reference)).filter(months => months !== null)
    const other = t('other_group')

    const band = (min, max) => {
        if (lifespans.length === 0) return ''
        const count = lifespans.filter(months => months >= min && (max === null || months <= max)).length
        return `${Math.round(count / lifespans.length * 100)}%`
    }

    const rows = [
        section(titleWithCurrency(`${t('stats_audience')} · ${monthLabel(period)}`)),
        blank(),
        [t('ltv'), average(withPlan
            .filter(client => clientLifespan(client, reference) !== null)
            .map(client => Number(client.planAmount) * clientLifespan(client, reference))) ?? ''],
        [t('monthly_ticket'), average(withPlan.map(client => Number(client.planAmount) || 0)) ?? ''],
        [t('seniority_0_3'), band(0, 3)],
        [t('seniority_4_6'), band(4, 6)],
        [t('seniority_7'), band(7, null)],
        [t('avg_seniority'), average(roster
            .map(client => clientLifespan(client, reference))
            .filter(months => months !== null)) ?? ''],
    ]

    const breakdown = (title, items) => {
        rows.push(blank())
        rows.push(section(title))
        rows.push([title, t('total')])
        items.forEach(item => rows.push([item.label, item.value]))
    }

    breakdown(t('gender'), topCounts(roster.map(client => client.gender), 3)
        .map(item => ({
            label: t(`gender_${{ M: 'male', F: 'female' }[item.label] || 'other'}`),
            value: item.value,
        })))
    breakdown(t('origin'), topCounts(roster.map(client => client.origin), 5)
        .map(item => ({
            label: i18n.te(`business.origin_${item.label}`) ? t(`origin_${item.label}`) : item.label,
            value: item.value,
        })))
    breakdown(t('age'), ageBands(roster))
    breakdown(t('sport'), topCounts(roster.map(client => sportLabel(client.sport)), 5, other))
    breakdown(t('area'), topCounts(roster.map(client => client.area), 7, other))

    rows.push(blank())
    rows.push(section(t('cohort_retention')))
    rows.push([
        t('cohort'),
        t('cohort_size'),
        ...Array.from({ length: COHORT_MONTHS }, (item, i) => `${i + 1}m`),
    ])
    buildCohorts(roster, period).forEach(row => {
        rows.push([
            row.period,
            row.total,
            ...row.values.map(value => (value === null ? '' : `${value}%`)),
        ])
    })

    return { name: t('stats_audience'), aoa: rows }
}

const buildMarketingSheet = (data) => {
    const { period, cash, months } = data
    const year = String(period).slice(0, 4)
    const funnel = cash?.funnel || {}
    const ads = getAdsInvestment(cash)
    const rate = (value) => (value === null ? '' : `${value}%`)

    const rows = [
        section(titleWithCurrency(`${t('stats_marketing')} · ${monthLabel(period)}`)),
        blank(),
        [t('ads_investment'), ads],
        [t('cac'), computeCac(ads, funnel.closes) ?? ''],
        [t('close_rate'), rate(computeRate(funnel.trials, funnel.closes))],
        blank(),
        section(t('funnel')),
        [t('inquiries'), funnel.inquiries ?? ''],
        [t('trials'), funnel.trials ?? ''],
        [t('closes'), funnel.closes ?? ''],
        [t('global_rate'), rate(computeRate(funnel.inquiries, funnel.closes))],
        blank(),
        section(t('evolution')),
        [t('period'), t('ads_investment'), t('inquiries'), t('trials'), t('closes'), t('cac')],
    ]

    monthsOfYear(year).forEach(key => {
        const monthCash = key === period ? cash : months[key]
        if (!monthCash) return
        const monthFunnel = monthCash.funnel || {}
        const monthAds = getAdsInvestment(monthCash)
        rows.push([
            key,
            monthAds,
            monthFunnel.inquiries ?? '',
            monthFunnel.trials ?? '',
            monthFunnel.closes ?? '',
            computeCac(monthAds, monthFunnel.closes) ?? '',
        ])
    })

    return { name: t('stats_marketing'), aoa: rows }
}

export const buildStatsSheets = (data) => [
    buildBasicSheet(data),
    buildFinancialSheet(data),
    buildAudienceSheet(data),
    buildMarketingSheet(data),
]

export const sheetName = (name) => String(name || 'Sheet').replace(/[:\\/?*[\]]/g, '').slice(0, 31)
